import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Alert,
  Button,
  ConfigProvider,
  Drawer,
  Empty,
  Select,
  Skeleton,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  FileExcelOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { ThreeMPhaseNode } from "../ThreeMConsolidation/threeMShared";
import PnlKpiCards from "./PnlKpiCards";
import PnlCharts from "./PnlCharts";
import PnlTopSheetTable from "./PnlTopSheetTable";
import WbsCodeToggle from "../../shared/WbsCodeToggle";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  FyOption,
  PnlRowKind,
  PnlTopSheetData,
  buildFyOptions,
  fmtINR,
  fmtINRCompact,
  fmtMonth,
  fyOf,
  fyOption,
  kindToTab,
  num,
} from "./pnlShared";

// Monthly Budget P&L ("Top Sheet") — the URI BUDGET F-1 sheet generated live:
// month columns from task schedules, inflow = scheduled qty × rate, outflow
// pillars = 3M sources scaled by month share. FY picker (India: Apr→Mar)
// defaults to the current fiscal year; clicking a month amount navigates to
// the Month Detail page (separate screen — a month can touch 900+ tasks).

const ALL_MONTHS = "__ALL__";

const MonthlyPnlReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { projectName: stateProjectName, id: stateProjectId } =
    (location.state as any) || {};
  const projectId = stateProjectId ?? params.projectId;

  const [data, setData] = useState<PnlTopSheetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
  const wbsName = useWbsName();
  const [activityId, setActivityId] = useState<number | undefined>(undefined);
  const [basePhases, setBasePhases] = useState<ThreeMPhaseNode[]>([]);

  // FY window — default: the current Indian fiscal year.
  const currentFy = fyOf(dayjs().format("YYYY-MM-01"));
  const [fyKey, setFyKey] = useState<string>(String(currentFy));

  const [refreshTick, setRefreshTick] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [unscheduledOpen, setUnscheduledOpen] = useState(false);
  const [cashDetailOpen, setCashDetailOpen] = useState(false);

  // Fullscreen: the report container goes fullscreen via the browser API
  // (pattern: ThreeMConsolidation). Popups and the unscheduled drawer are
  // re-targeted INTO the container — portals left on document.body would be
  // invisible while another element is fullscreen.
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    // Covers both the button and ESC (the browser exits without telling us).
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen is not supported in this browser");
      });
    }
  };

  const popupContainer = () => containerRef.current || document.body;

  // Fullscreen hand-off: arriving from the Month Detail page (or any
  // navigation that carries fullscreen: true) re-enters fullscreen on THIS
  // page's container — element fullscreen dies on route change, so each page
  // re-claims it while the click's transient activation is still valid.
  useEffect(() => {
    if ((location.state as any)?.fullscreen && !document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {
        /* no gesture left — degrade to normal view */
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgName: string | undefined = currentUser?.orgName;
  const orgLogoUrl: string | undefined = currentUser?.orgLogo;

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Phase/Activity options from one unfiltered consolidation load.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    threeMReportApis
      .get3MConsolidation(projectId)
      .then((response) => {
        if (!cancelled && response?.status) {
          setBasePhases(response?.data?.phases || []);
        }
      })
      .catch((err) =>
        console.error("Error loading phase/activity filter options:", err)
      );
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const fyWindow = useMemo((): { fromMonth?: string; toMonth?: string } => {
    if (fyKey === ALL_MONTHS) return {};
    const option = fyOption(Number(fyKey));
    return { fromMonth: option.fromMonth, toMonth: option.toMonth };
  }, [fyKey]);

  useEffect(() => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await threeMReportApis.getMonthlyPnlTopSheet(projectId, {
          ...fyWindow,
          phaseId,
          activityId,
        });
        const { status, data: payload } = response || {};
        if (cancelled) return;
        if (status) {
          setData(payload || {});
        } else {
          setError(response?.message || "Failed to load the Monthly P&L");
          message.error(response?.message || "Failed to load the Monthly P&L");
        }
      } catch (err) {
        console.error("Error loading Monthly Budget P&L:", err);
        if (!cancelled) {
          setError("Error loading Monthly Budget P&L");
          message.error("Error loading Monthly Budget P&L");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, phaseId, activityId, fyWindow, refreshTick]);

  const projectName = data?.projectName || stateProjectName || "Project";
  const months = useMemo(() => data?.months || [], [data]);

  const fyOptions = useMemo(() => {
    const options: Array<{ label: string; value: string }> = buildFyOptions(
      data?.scheduleRange?.firstMonth || undefined,
      data?.scheduleRange?.lastMonth || undefined
    ).map((option: FyOption) => ({
      label: option.label,
      value: String(fyOf(option.fromMonth)),
    }));
    // Current FY is always selectable even before schedules exist.
    if (!options.some((option) => option.value === String(currentFy))) {
      options.push({ label: fyOption(currentFy).label, value: String(currentFy) });
      options.sort((a, b) => Number(a.value) - Number(b.value));
    }
    options.push({ label: "Entire project", value: ALL_MONTHS });
    return options;
  }, [data, currentFy]);

  const phaseOptions = useMemo(
    () =>
      basePhases.map((phase) => ({
        label: wbsName(phase.phaseName),
        value: phase.phaseId,
      })),
    [basePhases, wbsName]
  );
  const activityOptions = useMemo(() => {
    if (!phaseId) return [];
    const phase = basePhases.find((p) => p.phaseId === phaseId);
    return (phase?.activities || []).map((activity) => ({
      label: wbsName(activity.activityName),
      value: activity.activityId,
    }));
  }, [basePhases, phaseId, wbsName]);

  const openMonthDetail = (month: string, kind: PnlRowKind) => {
    navigate(
      `/pms/project/monthly-pnl-month/${projectId}?month=${month}&tab=${kindToTab(kind)}`,
      {
        state: {
          ...(location.state || {}),
          id: projectId,
          projectName,
          phaseId,
          activityId,
          // carry fullscreen across the route change (see hand-off effect)
          fullscreen: isFullscreen,
        },
      }
    );
  };

  const handleExport = async () => {
    if (!projectId || !data) return;
    setExporting(true);
    try {
      const helpers = await import("../shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportWorkbook } = await import("./exportWorkbook");
      const phaseName = phaseId
        ? basePhases.find((p) => p.phaseId === phaseId)?.phaseName
        : undefined;
      const activityName =
        phaseId && activityId
          ? basePhases
              .find((p) => p.phaseId === phaseId)
              ?.activities?.find((a) => a.activityId === activityId)?.activityName
          : undefined;
      await exportWorkbook({
        data: { ...data, projectName },
        orgName,
        logo,
        fyLabel:
          fyKey === ALL_MONTHS ? "Entire project" : fyOption(Number(fyKey)).label,
        filters: { phaseName, activityName },
      });
      message.success("Excel exported");
    } catch (err) {
      console.error("Error exporting Monthly Budget P&L:", err);
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const unscheduledColumns: any[] = [
    { title: "Task ID", dataIndex: "taskSequenceId", key: "seq", width: 150, ellipsis: true },
    { title: "Task", dataIndex: "taskName", key: "name", ellipsis: true },
    {
      title: "Activity",
      dataIndex: "activityName",
      key: "activity",
      width: 150,
      ellipsis: true,
      render: (text: string) => wbsName(text),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 130,
      render: (value: string) =>
        value === "NO_SCHEDULE" ? (
          <Tag color="orange">No schedule</Tag>
        ) : (
          <Tag color="red">No work qty</Tag>
        ),
    },
    {
      title: "Invisible Inflow",
      dataIndex: "invisibleInflow",
      key: "inflow",
      width: 120,
      align: "right" as const,
      render: (value: string) => (
        <Tooltip title={fmtINR(value)}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINRCompact(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Outflow",
      dataIndex: "totalOutflow",
      key: "outflow",
      width: 120,
      align: "right" as const,
      render: (value: string) => (
        <Tooltip title={fmtINR(value)}>
          <b style={{ fontVariantNumeric: "tabular-nums", color: "#022c57" }}>
            {fmtINRCompact(value)}
          </b>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Head title="Top Sheet" />
      <Content>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            ...(isFullscreen
              ? { background: "#f5f6fa", overflowY: "auto", padding: 16 }
              : {}),
          }}
        >
        <ConfigProvider getPopupContainer={popupContainer}>
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  Top Sheet
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectName} · Month-wise P&L — inflow, 3M outflow and margin from task schedules
                </span>
              </div>
              <Space>
                <Button
                  icon={
                    isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
                  }
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exporting}
                  disabled={loading || !data || months.length === 0}
                  onClick={handleExport}
                >
                  Export
                </Button>
                <WbsCodeToggle />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {/* Filter bar */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
              <Select
                style={{ width: 170 }}
                options={fyOptions}
                value={fyKey}
                onChange={(value) => setFyKey(value)}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Phase"
                style={{ width: 220 }}
                options={phaseOptions}
                value={phaseId}
                onChange={(value) => {
                  setPhaseId(value);
                  setActivityId(undefined);
                }}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Activity"
                style={{ width: 220 }}
                options={activityOptions}
                value={activityId}
                disabled={!phaseId}
                onChange={(value) => setActivityId(value)}
              />
              {(phaseId || activityId) && (
                <Button
                  type="link"
                  onClick={() => {
                    setPhaseId(undefined);
                    setActivityId(undefined);
                  }}
                >
                  Clear filters
                </Button>
              )}
              {/* Unscheduled-cost health indicator — compact text, right of the filters */}
              {data && (
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                  {(data?.unscheduled?.taskCount || 0) > 0 ? (
                    <Button
                      type="link"
                      style={{ padding: 0, fontWeight: 600, color: "#b58a1f" }}
                      onClick={() => setUnscheduledOpen(true)}
                    >
                      <WarningOutlined style={{ marginRight: 6 }} />
                      Unscheduled cost:{" "}
                      {fmtINRCompact(
                        num(data?.unscheduled?.totalOutflow) +
                          num(data?.unscheduled?.invisibleInflow)
                      )}{" "}
                      · {data?.unscheduled?.taskCount} task(s) — view
                    </Button>
                  ) : (
                    <span style={{ color: "#389e0d", fontSize: 12.5, fontWeight: 600 }}>
                      <CheckCircleOutlined style={{ marginRight: 6 }} />
                      Full schedule coverage
                    </span>
                  )}
                </div>
              )}
            </div>

            {loading && !data ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : error && !data ? (
              <Empty description={error} style={{ padding: "60px 0" }} />
            ) : (
              <Spin spinning={loading}>
                <PnlKpiCards data={data} />

                {(data?.unscheduled?.taskCount || 0) > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${data?.unscheduled?.taskCount} task(s) carry money that is invisible in the month columns`}
                    description={
                      <span>
                        Tasks without a monthly schedule (or without a work quantity)
                        don't appear in any month.{" "}
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0 }}
                          onClick={() => setUnscheduledOpen(true)}
                        >
                          View the list
                        </Button>{" "}
                        and upload schedules to close the gap.
                      </span>
                    }
                  />
                )}

                {months.length === 0 ? (
                  <Empty
                    description={
                      fyKey === ALL_MONTHS
                        ? "No monthly schedules uploaded for this project yet"
                        : "No scheduled months in this fiscal year — try another FY or 'Entire project'"
                    }
                    style={{ padding: "40px 0" }}
                  />
                ) : (
                  <>
                    <PnlCharts
                      months={months}
                      onViewDetails={() => setCashDetailOpen(true)}
                    />
                    <PnlTopSheetTable
                      months={months}
                      totals={data?.totals}
                      onCellClick={openMonthDetail}
                    />
                    <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
                      Escalation, staff Manpower and Statutory heads show — until the
                      overhead data push; the Total column sums the visible months only.
                    </p>
                  </>
                )}
              </Spin>
            )}
          </div>
        </Card>
        </ConfigProvider>
        </div>
      </Content>

      <Drawer
        title="Month-on-month — Inflow vs Outflow vs Margin"
        placement="right"
        width="min(820px, 100vw)"
        open={cashDetailOpen}
        onClose={() => setCashDetailOpen(false)}
        getContainer={popupContainer}
        destroyOnClose
      >
        {(() => {
          let running = 0;
          const rows = months.map((column) => {
            running += num(column.margin);
            return { ...column, cumulativeMargin: running };
          });
          const moneyCell = (value: string | number, bold = false, signColor = false) => (
            <Tooltip title={fmtINR(value)}>
              <span
                style={{
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: bold ? 700 : 500,
                  color: signColor ? (num(value) >= 0 ? "#237804" : "#cf1322") : undefined,
                }}
              >
                {fmtINRCompact(value)}
              </span>
            </Tooltip>
          );
          return (
            <Table
              size="small"
              rowKey="month"
              dataSource={rows}
              pagination={false}
              columns={[
                {
                  title: "Month",
                  dataIndex: "month",
                  key: "month",
                  width: 110,
                  render: (value: string) => (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0 }}
                      onClick={() => {
                        setCashDetailOpen(false);
                        openMonthDetail(value, "totalOutflow");
                      }}
                    >
                      {fmtMonth(value)}
                    </Button>
                  ),
                },
                {
                  title: "Inflow",
                  dataIndex: "totalInflow",
                  key: "inflow",
                  align: "right" as const,
                  render: (value: string) => moneyCell(value),
                },
                {
                  title: "Outflow",
                  dataIndex: "totalOutflow",
                  key: "outflow",
                  align: "right" as const,
                  render: (value: string) => moneyCell(value, true),
                },
                {
                  title: "Margin",
                  dataIndex: "margin",
                  key: "margin",
                  align: "right" as const,
                  render: (value: string) => moneyCell(value, false, true),
                },
                {
                  title: "Margin %",
                  dataIndex: "marginPct",
                  key: "marginPct",
                  align: "right" as const,
                  width: 90,
                  render: (value: string) => (
                    <span style={{ fontVariantNumeric: "tabular-nums", color: "#526484" }}>
                      {num(value).toFixed(1)}%
                    </span>
                  ),
                },
                {
                  title: "Cumulative Margin",
                  dataIndex: "cumulativeMargin",
                  key: "cumulative",
                  align: "right" as const,
                  render: (value: number) => moneyCell(value, false, true),
                },
              ]}
              summary={() =>
                data?.totals ? (
                  <Table.Summary.Row style={{ background: "#f7f9fc" }}>
                    <Table.Summary.Cell index={0}>
                      <b>Total</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      {moneyCell(data.totals.totalInflow, true)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      {moneyCell(data.totals.totalOutflow, true)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {moneyCell(data.totals.margin, true, true)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <b style={{ fontVariantNumeric: "tabular-nums" }}>
                        {num(data.totals.marginPct).toFixed(1)}%
                      </b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {moneyCell(running, true, true)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                ) : null
              }
            />
          );
        })()}
        <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
          Click a month to open its full P&L detail. Cumulative margin is the running
          total across the visible months.
        </p>
      </Drawer>

      <Drawer
        title={`Unscheduled costs — ${data?.unscheduled?.taskCount || 0} task(s)`}
        placement="right"
        width="min(860px, 100vw)"
        open={unscheduledOpen}
        onClose={() => setUnscheduledOpen(false)}
        getContainer={popupContainer}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Why this list matters"
          description="These tasks have costs or billable quantity, but no month can claim them — either no schedule was uploaded, or the task has no work quantity so its month share is undefined. Their money is missing from every column of the top sheet."
        />
        <Table
          size="small"
          rowKey="taskId"
          columns={unscheduledColumns}
          dataSource={data?.unscheduled?.rows || []}
          pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
        />
      </Drawer>
    </>
  );
};

export default MonthlyPnlReport;
